import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Upload, FileText, Trash2, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateTime";
import { canVehicle } from "@/lib/permissions";

// Profile tab id -> permission key suffix (matrix keys are
// vehicles_renault_<suffix> / vehicles_otherbrands_<suffix>)
const TAB_PERM_SUFFIX = {
  vehicle: "vehicle",
  customer: "customer",
  insurance: "insurance",
  dates: "dates_programs",
  documents: "documents",
  dealer: "dealer",
};

// Helper to display value or dash
const DisplayValue = ({ value }) => (
  <span className="text-sm">{value || "—"}</span>
);

// Field row component
const FieldRow = ({ label, value }) => (
  <div className="py-2 border-b border-gray-100 last:border-0">
    <Label className="text-xs text-gray-500 uppercase tracking-wider">{label}</Label>
    <p className="mt-1"><DisplayValue value={value} /></p>
  </div>
);

// Section component for grouping fields
const Section = ({ title, children }) => (
  <div className="mb-6">
    {title && <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">{title}</h3>}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8">
      {children}
    </div>
  </div>
);

const VehicleProfile = ({ brand = "renault" }) => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isRenault = brand === "renault";
  const permPrefix = isRenault ? "vehicles_renault_" : "vehicles_otherbrands_";
  const permKeyFor = (tab) => `${permPrefix}${TAB_PERM_SUFFIX[tab]}`;

  // Preserve the tab the user came from (e.g. after returning from Edit).
  const allowedTabs = isRenault
    ? ["vehicle", "customer", "insurance", "dates", "documents", "dealer"]
    : ["vehicle", "customer", "insurance", "dates", "documents"];
  // Only tabs the user can view (matrix "view" tick, master cascade) are shown.
  const viewableTabs = allowedTabs.filter((t) => canVehicle(user, permKeyFor(t), "view"));
  const requestedTab = searchParams.get("tab");
  const initialTab = viewableTabs.includes(requestedTab) ? requestedTab : viewableTabs[0];

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [docType, setDocType] = useState("insurance");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingVehicle, setDeletingVehicle] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchVehicle = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/vehicles/${vehicleId}`, { withCredentials: true });
        setVehicle(res.data);
      } catch (e) {
        console.error("Failed to fetch vehicle", e);
      } finally {
        setLoading(false);
      }
    };
    if (vehicleId) fetchVehicle();
  }, [vehicleId]);

  // Fetch documents for this vehicle
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await axios.get(`${API}/vehicles/${vehicleId}/documents`, { withCredentials: true });
        setDocuments(res.data);
      } catch (e) {
        console.error("Failed to fetch documents", e);
      }
    };
    if (vehicleId) fetchDocuments();
  }, [vehicleId]);

  const handleBack = () => {
    navigate(isRenault ? "/vehicles/renault" : "/vehicles/other-brands");
  };

  // Document handlers
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      // document_type is a query param on the backend, not a form field.
      const res = await axios.post(
        `${API}/vehicles/${vehicleId}/documents?document_type=${docType}`,
        formData,
        { 
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      setDocuments([res.data, ...documents]);
      toast.success("Document uploaded successfully");
      setUploadOpen(false);
      setPendingFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (deletingDocId) return;
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    setDeletingDocId(docId);
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}/documents/${docId}`, { withCredentials: true });
      setDocuments(documents.filter(d => d.document_id !== docId));
      toast.success("Document deleted");
    } catch (err) {
      toast.error("Failed to delete document");
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleDeleteVehicle = async () => {
    if (deletingVehicle) return;
    if (!window.confirm("Are you sure you want to delete this vehicle? This action cannot be undone.")) return;
    setDeletingVehicle(true);
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { withCredentials: true });
      toast.success("Vehicle deleted successfully");
      handleBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete vehicle");
      setDeletingVehicle(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Vehicle not found</p>
        <Button variant="outline" className="mt-4 rounded-sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Vehicles
        </Button>
      </div>
    );
  }

  // Determine which tabs to show (permission-filtered above)
  const tabs = viewableTabs;
  const canEditActiveTab = activeTab ? canVehicle(user, permKeyFor(activeTab), "edit") : false;
  const canUploadDocuments = canVehicle(user, permKeyFor("documents"), "update");
  const canEditDocuments = canVehicle(user, permKeyFor("documents"), "edit");
  // Rule: deleting a vehicle requires Master (cascades within the group).
  const canDeleteVehicle = canVehicle(user, permKeyFor("vehicle"), "master");

  if (viewableTabs.length === 0) {
    return (
      <div className="text-center py-12" data-testid="no-vehicle-tabs-access">
        <p className="text-2xl font-bold text-gray-300">No access to vehicle details</p>
        <p className="text-sm text-gray-400 mt-2">Ask an administrator to grant you Vehicles permissions.</p>
        <Button variant="outline" className="mt-4 rounded-sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Vehicles
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vehicle-profile">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <div>
            <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tighter uppercase">
              Vehicle Profile
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {vehicle.vehicle_reg_no || "—"} • {vehicle.vin || "—"} • Verified on{" "}
              <span data-testid="verified-on">
                {vehicle.ownership_verified_on
                  ? formatDate(vehicle.ownership_verified_on)
                  : "—"}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEditActiveTab && (
          <Button variant="outline" size="icon" className="rounded-sm" onClick={() => navigate(`/vehicles/${isRenault ? 'renault' : 'other-brands'}/${vehicleId}/edit?tab=${activeTab}`)} data-testid="edit-btn">
            <Pencil className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          )}
          {canDeleteVehicle && (
          <Button variant="outline" size="icon" className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDeleteVehicle} disabled={deletingVehicle} data-testid="delete-btn">
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {tabs.map((tab) => {
            const tabLabels = {
              vehicle: "Vehicle",
              customer: "Customer",
              insurance: "Insurance",
              dates: "Dates & Programs",
              documents: "Documents",
              dealer: "Dealer"
            };
            return (
              <Button
                key={tab}
                variant="ghost"
                className={`px-4 py-2 rounded-none border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab
                    ? "border-black text-black"
                    : "border-transparent text-gray-500 hover:text-black hover:border-gray-300"
                }`}
                onClick={() => setActiveTab(tab)}
                data-testid={`tab-${tab}`}
              >
                {tabLabels[tab]}
              </Button>
            );
          })}
        </div>
      </div>

        {/* TAB 1 — Vehicle (combined with Service) */}
        {activeTab === "vehicle" && (
          <div className="mt-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <Section title="Vehicle Details">
                  <FieldRow label="Brand" value={isRenault ? "Renault" : vehicle.make} />
                  <FieldRow label="VIN #" value={vehicle.vin} />
                  <FieldRow label="Engine #" value={vehicle.engine_no} />
                  <FieldRow label="Temporary/Registration No" value={vehicle.vehicle_reg_no} />
                  <FieldRow label="Model Description" value={vehicle.model} />
                  {isRenault && (
                    <>
                      <FieldRow label="Variant Description" value={vehicle.variant_description} />
                      <FieldRow label="Model Code" value={vehicle.model_code} />
                      <FieldRow label="Color Type" value={vehicle.color_type} />
                      <FieldRow label="Exterior Colour" value={vehicle.exterior_colour} />
                      <FieldRow label="Exterior Colour Code" value={vehicle.exterior_colour_code} />
                    </>
                  )}
                </Section>
                <Section title="Service">
                  <FieldRow label="Last Service Date" value={vehicle.last_service_date} />
                  <FieldRow label="Next Service Due" value={vehicle.next_service_due} />
                  <FieldRow label="Last Odometer Reading" value={vehicle.last_odometer_reading} />
                </Section>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 2 — Customer */}
        {activeTab === "customer" && (
          <div className="mt-6 space-y-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <Section>
                  <FieldRow label="Contact Name" value={vehicle.customer_name} />
                  <FieldRow label="Phone" value={vehicle.customer_phone} />
                  <FieldRow label="Email" value={vehicle.customer_email || vehicle.email} />
                  <FieldRow label="Alternate No" value={vehicle.alternate_no} />
                  <FieldRow label="Contact Address" value={vehicle.contact_address || vehicle.address} />
                  <FieldRow label="Contact Address2" value={vehicle.contact_address2} />
                  <FieldRow label="Contact City" value={vehicle.contact_city || vehicle.city} />
                  <FieldRow label="Contact District" value={vehicle.contact_district} />
                  <FieldRow label="Contact State" value={vehicle.contact_state || vehicle.state} />
                  <FieldRow label="Contact Country" value={vehicle.contact_country} />
                  <FieldRow label="Contact Zipcode" value={vehicle.contact_zipcode || vehicle.pin} />
                </Section>
                <Section>
                  <FieldRow label="Date of Birth" value={vehicle.date_of_birth} />
                  <FieldRow label="Anniversary" value={vehicle.anniversary} />
                  <FieldRow label="Driven By" value={vehicle.driven_by} />
                  <FieldRow label="Preferred Contact Mode" value={(vehicle.preferred_contact_mode || []).join(", ")} />
                  <FieldRow label="Preferred Contact Time" value={(vehicle.preferred_contact_time || []).join(", ")} />
                </Section>
              </CardContent>
            </Card>

            {/* Customer change history */}
            <ChangeHistorySection entries={vehicle.change_history || []} />
          </div>
        )}

        {/* TAB 3 — Insurance */}
        {activeTab === "insurance" && (
          <div className="mt-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <Section title="Own Damage (OD)">
                  <FieldRow label="OD Policy Start Date" value={vehicle.od_policy_start_date} />
                  <FieldRow label="OD Policy End Date" value={vehicle.od_policy_end_date} />
                  <FieldRow label="Own Damage Insurer" value={vehicle.od_insurer} />
                  <FieldRow label="OD Policy Number" value={vehicle.od_policy_number} />
                </Section>
                <Section title="Third Party (TP)">
                  <FieldRow label="TP Policy Start Date" value={vehicle.tp_policy_start_date} />
                  <FieldRow label="TP Policy End Date" value={vehicle.tp_policy_end_date} />
                  <FieldRow label="Third Party Insurer" value={vehicle.tp_insurer} />
                  <FieldRow label="TP Policy Number" value={vehicle.tp_policy_number} />
                </Section>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 4 — Dates & Programs */}
        {activeTab === "dates" && (
          <div className="mt-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <Section title="Common Dates">
                  <FieldRow label="Invoiced Date" value={vehicle.invoiced_date} />
                  <FieldRow label="Vehicle Delivery Date" value={vehicle.delivery_date} />
                  <FieldRow label="VIN Mfg Year" value={vehicle.vin_mfg_year} />
                  <FieldRow label="Warranty Start Date" value={vehicle.warranty_start_date} />
                  <FieldRow label="Warranty End Date" value={vehicle.warranty_end_date} />
                  <FieldRow label="Warranty End Km" value={vehicle.warranty_end_km} />
                </Section>

                {/* Renault-only fields */}
                {isRenault && (
                  <>
                    <Section title="Extended Warranty">
                      <FieldRow label="Extended Warranty Start Date" value={vehicle.ext_warranty_start_date} />
                      <FieldRow label="Extended Warranty End Date" value={vehicle.ext_warranty_end_date} />
                      <FieldRow label="Extended Warranty End Km" value={vehicle.ext_warranty_end_km} />
                    </Section>
                    <Section title="Easy Care">
                      <FieldRow label="Easy Care Name" value={vehicle.easy_care_name} />
                      <FieldRow label="Easy Care Start Date" value={vehicle.easy_care_start_date} />
                      <FieldRow label="Easy Care End Date" value={vehicle.easy_care_end_date} />
                      <FieldRow label="Easy Care End KM" value={vehicle.easy_care_end_km} />
                    </Section>
                    <Section title="RSA (Roadside Assistance)">
                      <FieldRow label="RSA Product Name" value={vehicle.rsa_product_name} />
                      <FieldRow label="RSA Certification Number" value={vehicle.rsa_certification_number} />
                      <FieldRow label="RSA Start Date" value={vehicle.rsa_start_date} />
                      <FieldRow label="RSA End Date" value={vehicle.rsa_end_date} />
                    </Section>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 5 — Documents */}
        {activeTab === "documents" && (
          <div className="mt-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                {/* Upload Section */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Vehicle Documents</h3>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingFile(f); e.target.value = ""; }}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                    <Button
                      variant="outline"
                      className="rounded-sm"
                      onClick={() => setUploadOpen(true)}
                      disabled={uploading || !canUploadDocuments}
                      data-testid="upload-document-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {uploading ? "Uploading..." : "Upload Document"}
                    </Button>
                  </div>
                </div>

                <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) setPendingFile(null); }}>
                  <DialogContent className="rounded-sm sm:max-w-md" data-testid="upload-document-modal">
                    <DialogHeader>
                      <DialogTitle>Add Document</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-gray-400">Document Type</Label>
                        <Select value={docType} onValueChange={setDocType}>
                          <SelectTrigger className="rounded-sm" data-testid="document-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="insurance">Insurance</SelectItem>
                            <SelectItem value="rc">Registration (RC)</SelectItem>
                            <SelectItem value="general">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-gray-400">File</Label>
                        <div className="flex items-center gap-3">
                          <Button variant="outline" size="sm" className="rounded-sm"
                            onClick={() => fileInputRef.current?.click()} data-testid="choose-file-btn">
                            <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            {pendingFile ? "Change File" : "Choose File"}
                          </Button>
                          <span className="text-xs text-gray-500 truncate max-w-[200px]">
                            {pendingFile ? pendingFile.name : "No file chosen"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" className="rounded-sm"
                        onClick={() => { setUploadOpen(false); setPendingFile(null); }}>Cancel</Button>
                      <Button className="rounded-sm bg-black text-white hover:bg-gray-800"
                        disabled={!pendingFile || uploading}
                        onClick={() => handleFileUpload(pendingFile)} data-testid="confirm-upload-btn">
                        {uploading ? "Uploading..." : "Upload"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Documents List */}
                {documents.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" strokeWidth={1} />
                    <p className="text-sm">No documents uploaded yet</p>
                    <p className="text-xs text-gray-400 mt-1">Upload RC, Insurance, or other vehicle documents</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-sm hover:bg-gray-50"
                        data-testid={`document-${doc.document_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-sm flex items-center justify-center">
                            <FileText className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{doc.file_name}</p>
                            <p className="text-xs text-gray-500">
                              {doc.document_type} • {(doc.file_size / 1024).toFixed(1)} KB • {formatDate(doc.uploaded_at?.split("T")[0])}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.file_url && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-sm"
                                onClick={() => window.open(doc.file_url, "_blank")}
                                data-testid={`view-doc-${doc.document_id}`}
                              >
                                <Eye className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-sm"
                                onClick={() => {
                                  const link = document.createElement("a");
                                  link.href = doc.file_url;
                                  link.download = doc.file_name;
                                  link.click();
                                }}
                                data-testid={`download-doc-${doc.document_id}`}
                              >
                                <Download className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-sm text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                            disabled={!canEditDocuments || deletingDocId === doc.document_id}
                            data-testid={`delete-doc-${doc.document_id}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 6 — Dealer (Renault only) */}
        {isRenault && activeTab === "dealer" && (
          <div className="mt-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <Section>
                  <FieldRow label="Dealer Code" value={vehicle.dealer_code} />
                  <FieldRow label="Dealer Name" value={vehicle.dealer_name} />
                  <FieldRow label="Dealer Group" value={vehicle.dealer_group} />
                  <FieldRow label="Last Servicing Dealer" value={vehicle.last_servicing_dealer} />
                  <FieldRow label="Penultimate Servicing Dealer" value={vehicle.penultimate_servicing_dealer} />
                </Section>
              </CardContent>
            </Card>
          </div>
        )}
    </div>
  );
};

export default VehicleProfile;

// ============== ChangeHistorySection ==============
// Field-tagged audit trail for customer-only edits (name, phone, address, owner, email).

const FIELD_PILLS = {
  phone:   { label: "Phone",   className: "bg-blue-100 text-blue-800 border-blue-200" },
  name:    { label: "Name",    className: "bg-amber-100 text-amber-800 border-amber-200" },
  address: { label: "Address", className: "bg-gray-100 text-gray-800 border-gray-200" },
  email:   { label: "Email",   className: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  other:   { label: "Other",   className: "bg-gray-100 text-gray-800 border-gray-200" },
};

function ChangeHistorySection({ entries }) {
  const [filter, setFilter] = useState("all");

  // Treat any legacy "owner" entries as "name" on read — we no longer
  // distinguish corrections from ownership changes.
  const normalizedEntries = entries.map((e) => ({
    ...e,
    field: e.field === "owner" ? "name" : (e.field || "other"),
  }));

  const chips = [
    { id: "all",     label: "All" },
    { id: "name",    label: "Name" },
    { id: "phone",   label: "Phone" },
    { id: "address", label: "Address" },
    { id: "email",   label: "Email" },
  ];

  const filtered = filter === "all"
    ? normalizedEntries
    : normalizedEntries.filter((e) => e.field === filter);

  const formatTime24 = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  return (
    <Card className="border border-gray-200 rounded-sm shadow-none" data-testid="change-history-card">
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-4">
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
              Change history
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Every edit to this vehicle&apos;s customer details
            </p>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="change-history-filter">
          {chips.map((c) => {
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setFilter(c.id)}
                className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                  active
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
                data-testid={`history-chip-${c.id}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Entries */}
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="no-change-history">
            {entries.length === 0 ? "No changes recorded yet." : "No changes match this filter."}
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((e, idx) => {
              const pill = FIELD_PILLS[e.field] || FIELD_PILLS.other;
              return (
                <div
                  key={`${e.changed_at}-${idx}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-gray-200 rounded-sm p-3"
                  data-testid={`change-history-entry-${idx}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm shrink-0 ${pill.className}`}
                      data-testid={`history-pill-${e.field}`}
                    >
                      {pill.label}
                    </span>
                    <div className="min-w-0 text-sm">
                      <span className="line-through text-gray-400 break-all">{e.old_value}</span>
                      <span className="text-gray-400 mx-2">→</span>
                      <span className="font-semibold break-all">{e.new_value}</span>
                      {e.note && (
                        <span className="block text-xs text-gray-500 mt-0.5">{e.note}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 shrink-0 sm:text-right">
                    {formatDate(e.changed_at?.split("T")[0])} • {formatTime24(e.changed_at)}
                    <span className="block">by {e.changed_by || "System"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer count */}
        <p className="text-xs text-gray-400 mt-4 text-right" data-testid="change-history-count">
          {entries.length} change{entries.length === 1 ? "" : "s"} recorded
        </p>
      </CardContent>
    </Card>
  );
}
